import React, { useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconMail, IconFileText, IconUsers, IconZap } from '../components/Icons';
import { useAuth } from '../auth';
import PublicHeader from '../components/PublicHeader';
import { capture } from '../analytics';

export default function LandingPage() {
  const { t } = useTranslation();
  const { loading, user } = useAuth();

  useEffect(() => { capture('landing_page_viewed'); }, []);

  if (!loading && user) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const features = [
    {
      icon: <IconMail size={20} />,
      title: t('landing.features.subscriptionTitle'),
      description: t('landing.features.subscriptionDesc'),
    },
    {
      icon: <IconFileText size={20} />,
      title: t('landing.features.invoiceTitle'),
      description: t('landing.features.invoiceDesc'),
    },
    {
      icon: <IconUsers size={20} />,
      title: t('landing.features.accountTitle'),
      description: t('landing.features.accountDesc'),
    },
    {
      icon: <IconZap size={20} />,
      title: t('landing.features.repliesTitle'),
      description: t('landing.features.repliesDesc'),
    },
  ];

  return (
    <div className="landing">
      <PublicHeader />

      <section className="hero">
        <div className="hero-badge">{t('landing.heroBadge')}</div>

        <h1>
          {t('landing.heroTitle')}<br />
          <em>{t('landing.heroTitleEm')}</em>
        </h1>

        <p className="hero-subtitle">{t('landing.heroSubtitle')}</p>

        <div className="hero-actions">
          <a href="/.auth/login/google?post_login_redirect_uri=/app/dashboard" className="auth-btn auth-btn-google" onClick={() => capture('sign_in_clicked', { provider: 'google' })}>
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {t('landing.signInGoogle')}
          </a>
          <a href="/.auth/login/aad?post_login_redirect_uri=/app/dashboard" className="auth-btn auth-btn-microsoft" onClick={() => capture('sign_in_clicked', { provider: 'microsoft' })}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="1" y="1" width="10" height="10" fill="#f25022"/>
              <rect x="13" y="1" width="10" height="10" fill="#7fba00"/>
              <rect x="1" y="13" width="10" height="10" fill="#00a4ef"/>
              <rect x="13" y="13" width="10" height="10" fill="#ffb900"/>
            </svg>
            {t('landing.signInMicrosoft')}
          </a>
        </div>

        <div className="features">
          {features.map(f => (
            <div key={f.title} className="feature-card">
              <div className="feature-icon">{f.icon}</div>
              <h3>{f.title}</h3>
              <p>{f.description}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="landing-footer">
        {t('common.copyright', { year: new Date().getFullYear() })} ·{' '}
        <Link to="/privacy">{t('landing.footer.privacy')}</Link> ·{' '}
        <Link to="/terms">{t('landing.footer.terms')}</Link>
      </footer>
    </div>
  );
}
